import { Icon20CalendarOutline, Icon20InfoCircleOutline } from '@vkontakte/icons';
import { Button as ButtonVKUI, Input, Spacing, Text } from '@vkontakte/vkui';
import classNames from 'clsx';
import { parse, stringify } from 'qs';
import React, { memo, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { activatePromocodeRequest } from 'reactApp/modules/quotaLanding/quotaLanding.module';
import { historyPush } from 'reactApp/modules/router/router.module';
import { getIdByStorage } from 'reactApp/modules/storage/storage.helpers';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { usePromocodeDetails } from 'reactApp/sections/QuotaLanding/PromocodeDialog/hooks/usePromocodeDetails';
import type { PromocodeDialogProps } from 'reactApp/sections/QuotaLanding/PromocodeDialog/PromocodeDialog.types';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';
import { Dialog } from 'reactApp/ui/Dialog/Dialog';
import { type EPromocodeApiErrorCodes, errorShort } from 'reactApp/ui/Promocode/promocode.constants';
import { sendDwh } from 'reactApp/utils/ga';
import opener from 'reactApp/utils/opener';

import styles from './PromocodeDialog.css';

const sendRadar = (action: string, label?: string) => {
    sendDwh({
        eventCategory: 'modal-promocodes',
        action,
        label,
        dwhData: {
            platform: 'desktop',
        },
    });
};

export const PromocodeDialog = memo<PromocodeDialogProps>(({ onClose }) => {
    const dispatch = useDispatch();

    const email = useSelector(UserSelectors.getEmail);
    const { title, promocode, conditions } = usePromocodeDetails();
    const [promocodeValue, setPromocode] = useState(promocode);
    const [isActivated, setActivated] = useState(false);
    const [isInProgress, setInProgress] = useState(false);
    const [error, setError] = useState<EPromocodeApiErrorCodes | null>(null);
    const { search, pathname } = useLocation();

    const onActivate = useCallback(() => {
        setActivated(true);
        setInProgress(false);
    }, [setActivated, setInProgress]);

    const onError = useCallback(
        (code: EPromocodeApiErrorCodes) => {
            setInProgress(false);
            setError(code);
        },
        [setInProgress, setError]
    );

    const handleChangePromocode = useCallback(
        (event) => {
            const { value } = event.target || {};
            setPromocode(value);
            if (error) {
                setError(null);
            }
        },
        [setPromocode, setError, error]
    );

    const handleActivate = useCallback(() => {
        if (promocodeValue) {
            sendRadar('click', 'activate');
            setInProgress(true);
            dispatch(
                activatePromocodeRequest({
                    promocode: promocodeValue,
                    onActivate,
                    onError,
                })
            );
        }
    }, [promocodeValue, dispatch, setInProgress, onActivate, onError]);

    const handleChangeAccount = useCallback(() => {
        setTimeout(() => {
            window.__PH?.authMenu.show();
        }, 0);
        onClose?.();
        window.scrollTo({ top: 0 });
        const query = parse(search, { ignoreQueryPrefix: true }) || {};
        query.action = 'request-promocode';
        dispatch(
            historyPush({
                id: pathname,
                search: stringify(query, { addQueryPrefix: true }),
            })
        );
    }, [onClose, dispatch, search, pathname]);

    const handleGoToSubscriptions = useCallback(() => {
        onClose?.();
        opener(getIdByStorage(EStorageType.subscriptions), false);
    }, [onClose]);

    const handleClose = useCallback(() => {
        sendRadar('close');
        onClose?.();
    }, [onClose]);

    useEffect(() => {
        sendRadar('show');
    }, []);

    return (
        <Dialog open id="promocode-modal" closable size="large" bigCloseIcon className={styles.root} onCancel={handleClose}>
            <div className={styles.content}>
                <Spacing size={87} />
                {title}
                {isActivated ? (
                    <>
                        <Spacing size={46} />
                        <Text className={styles.activated}>
                            Промокод добавлен на страницу «Мои подписки».
                            <br />
                            Перейдите, чтобы завершить активацию
                        </Text>
                        <Spacing size={16} />
                        <Button
                            theme="vk"
                            sizeMode={ButtonSizeMode.middle}
                            className={styles.buttonSubscriptions}
                            onClick={handleGoToSubscriptions}
                        >
                            Мои подписки
                        </Button>
                    </>
                ) : (
                    <>
                        <Spacing size={74} />
                        <div className={styles.form}>
                            <Input
                                type="text"
                                placeholder="Введите промокод"
                                value={promocodeValue}
                                className={classNames(styles.input, {
                                    [styles.inputError]: Boolean(error),
                                })}
                                onChange={handleChangePromocode}
                            />
                            <Button
                                theme="vk"
                                sizeMode={ButtonSizeMode.big}
                                className={styles.button}
                                onClick={handleActivate}
                                disabled={!promocodeValue || isInProgress}
                            >
                                Активировать
                            </Button>
                        </div>
                        {error && (
                            <>
                                <Spacing size={12} />
                                <Text className={styles.error}>{errorShort[error]}</Text>
                            </>
                        )}
                    </>
                )}
                {Boolean(email) && !isActivated && (
                    <>
                        <Spacing size={16} />
                        <Text className={styles.description}>
                            Промокод будет активирован для аккаунта{' '}
                            <ButtonVKUI mode="link" className={styles.buttonLink} title="Сменить аккаунт" onClick={handleChangeAccount}>
                                {email}
                            </ButtonVKUI>
                        </Text>
                    </>
                )}
                <Spacing size={60} />
            </div>
            {conditions && (
                <>
                    <div className={styles.conditions}>
                        {conditions.map((item, index) => {
                            const icon = index ? <Icon20InfoCircleOutline /> : <Icon20CalendarOutline />;
                            return (
                                <div className={styles.condition} key={`condition-${index}`}>
                                    {icon}
                                    <Text className={styles.conditionText}>{item}</Text>
                                </div>
                            );
                        })}
                    </div>
                    <Spacing size={16} />
                    <ButtonVKUI
                        mode="link"
                        href="https://promoimages.hb.ru-msk.vkcs.cloud/promocodes_terms.pdf"
                        target="_blank"
                        className={styles.fullConditions}
                    >
                        Полные условия
                    </ButtonVKUI>
                    <Spacing size={32} />
                </>
            )}
        </Dialog>
    );
});

PromocodeDialog.displayName = 'PromocodeDialog';
